/**
 * Carrier autodetect, in the browser.
 *
 * The tracking form guesses the carrier from the number as it is pasted, so the operator
 * does not have to pick UPS off a list forty times a day. The rules mirror
 * `apps/api/src/modules/orders/domain/carrier.ts` exactly, and the server is still the
 * authority: it re-detects on the write, so a wrong guess here is corrected there rather
 * than stored.
 *
 * Kept here rather than imported from the API because `@elutide/shared` is the only thing
 * the web may reach into, and a carrier table is not a wire contract.
 */
import type { Carrier } from '@elutide/shared';

/** Strip the spaces and dashes a pasted tracking number usually carries. */
export function normaliseTracking(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Detect from the number alone. Order matters: the UPS 1Z form is unambiguous, and a 9x
 * USPS prefix wins even on a UPS shipment, because UPS Mail Innovations and SurePost hand
 * off to USPS and the number only resolves on the USPS site.
 */
export function detectCarrier(raw: string | null | undefined): Carrier {
  if (!raw) return 'other';
  const t = normaliseTracking(raw);
  if (/^1Z[0-9A-Z]{16}$/.test(t)) return 'ups';
  if (/^9[0-9]{19,21}$/.test(t)) return 'usps';
  if (/^(94|93|92|82)[0-9]{18,20}$/.test(t)) return 'usps';
  if (/^[0-9]{12}$/.test(t) || /^[0-9]{15}$/.test(t)) return 'fedex';
  if (/^[0-9]{10}$/.test(t)) return 'dhl';
  if (/^(T|[0-9])[0-9]{9}$/.test(t)) return 'dhl';
  return 'other';
}

export const CARRIER_LABEL: Readonly<Record<Carrier, string>> = {
  ups: 'UPS',
  usps: 'USPS',
  fedex: 'FedEx',
  dhl: 'DHL',
  other: 'Other',
};

export const CARRIER_OPTIONS: readonly { value: Carrier; label: string }[] = [
  { value: 'ups', label: 'UPS' },
  { value: 'usps', label: 'USPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'dhl', label: 'DHL' },
  { value: 'other', label: 'Other' },
];
